'use client';

import dynamic from 'next/dynamic';
import { useInView } from 'motion/react';
import { memo, useMemo, useRef, type ReactNode } from 'react';
import { cn } from '../cn';
import { useDeviceDetection } from '../hooks/use-device-detection';

type ScrollFadeInProps = {
  children: ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right';
  delay?: number;
  duration?: number;
  threshold?: number;
  triggerOnce?: boolean;
  className?: string;
  distance?: number;
};

const MotionDiv = dynamic(
  () => import('motion/react').then(module => module.motion.div),
  { ssr: true }
);

export const ScrollFadeIn = memo(function ScrollFadeIn({
  children,
  direction = 'down',
  delay = 0,
  duration = 0.6,
  threshold = 0.1,
  triggerOnce = true,
  className,
  distance = 60,
}: ScrollFadeInProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: triggerOnce,
    amount: threshold,
  });
  const { isDesktop } = useDeviceDetection();
  const resolvedDistance = isDesktop ? distance : 0;
  const variants = useMemo(
    () => ({
      hidden: {
        opacity: 0,
        x:
          direction === 'left'
            ? -resolvedDistance
            : direction === 'right'
              ? resolvedDistance
              : 0,
        y:
          direction === 'up'
            ? -resolvedDistance
            : direction === 'down'
              ? resolvedDistance
              : 0,
      },
      visible: {
        opacity: 1,
        x: 0,
        y: 0,
      },
    }),
    [direction, resolvedDistance]
  );
  const transition = useMemo(
    () => ({
      duration,
      delay: delay / 1000,
      ease: 'easeOut' as const,
    }),
    [delay, duration]
  );

  return (
    <MotionDiv
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={variants}
      transition={transition}
      className={cn(className)}
    >
      {children}
    </MotionDiv>
  );
});
